import { useEffect } from "react";
import { Autocomplete, Box, Stack, TextField, Typography } from "@mui/material";
import typography from "../../../../cssUtils/typography";
import { ColorsConstants } from "../../../../cssUtils/constants";

const STAGE_OPTIONS = [
  { value: "reminder_1", label: "Reminder 1" },
  { value: "reminder_2", label: "Reminder 2" },
  { value: "reminder_3", label: "Reminder 3" },
  { value: "reminder_4", label: "Reminder 4" },
  { value: "reminder_5", label: "Reminder 5" },
];

const YES_NO_OPTIONS = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

const TIMING_DIRECTION_OPTIONS = [
  { value: "before", label: "Days before due" },
  { value: "after", label: "Days after due" },
];

const LIFE_FIELD_OPTIONS = [
  { value: "customer_name", label: "Customer Name" },
  { value: "policy_number", label: "Policy Number" },
  { value: "premium_amount", label: "Premium Amount" },
  { value: "sum_assured", label: "Sum Assured" },
  { value: "plan", label: "Plan" },
  { value: "insurer", label: "Insurer" },
  { value: "frequency", label: "Frequency" },
  { value: "due_date", label: "Due Date" },
  { value: "tenant_name", label: "Business Name" },
];

// Sequential {{1}}, {{2}}, ... count, matching how the backend
// (templates.gupshup_helper.get_required_fields) counts body placeholders.
const getPlaceholderCount = (template) => {
  const content = template?.template_json?.content || "";
  let count = 0;
  while (content.includes(`{{${count + 1}}}`)) count += 1;
  return count;
};

const LIFE_SAMPLE_VALUES = {
  customer_name: "Rahul Sharma",
  policy_number: "LIC-1234567",
  premium_amount: "12,000",
  sum_assured: "10,00,000",
  plan: "Term Life Secure",
  insurer: "LIC",
  frequency: "Monthly",
  due_date: "24/07/2026",
  tenant_name: "FinTalq",
};

const buildPreviewText = (template, mapping) => {
  let content = template?.template_json?.content || "";
  mapping.forEach((field, index) => {
    const sample = LIFE_SAMPLE_VALUES[field] || `{{${index + 1}}}`;
    content = content.split(`{{${index + 1}}}`).join(sample);
  });
  return content;
};

const SendLifeInsuranceReminderPreset = ({ config, handleConfigChange, templates }) => {
  const selectedTemplate =
    templates?.find((t) => t?.template_id === config["template_id"]) || null;
  const placeholderCount = getPlaceholderCount(selectedTemplate);
  const variableMapping = config["variable_mapping"] || [];
  // Reminder 1 is mandatory and always enabled regardless of its own config value
  // (see handleStageChange/the Enabled field below) — mirrors the same "Enabled"
  // check used everywhere else here.
  const isEnabled = config["stage"] === "reminder_1" ? true : !!config["enabled"];

  const handleTemplateChange = (newValue) => {
    handleConfigChange("template_id", newValue?.template_id);
    // Re-sync mapping length to the newly selected template's placeholder count.
    const nextCount = getPlaceholderCount(newValue);
    const next = Array.from(
      { length: nextCount },
      (_, i) => variableMapping[i] || "",
    );
    handleConfigChange("variable_mapping", next);
  };

  const handleMappingChange = (index, value) => {
    const next = [...variableMapping];
    next[index] = value;
    handleConfigChange("variable_mapping", next);
  };

  const handleStageChange = (newStage) => {
    handleConfigChange("stage", newStage);
    // Reminder 1 is mandatory and always enabled — its Enabled toggle is locked
    // to Yes in the UI below, so make sure that's actually saved, not just shown.
    if (newStage === "reminder_1") {
      handleConfigChange("enabled", true);
    }
  };

  // The Enabled/Timing Direction fields below show a default selection (Yes for
  // Reminder 1, "Days before due") even when config has no value yet — but a
  // displayed default is not the same as a saved one. Without this, saving a
  // freshly-created node (or one whose config predates these fields) sends a
  // config missing "enabled"/"timing_direction" entirely, which the backend's
  // schema rejects (see nodebot/utils.py's send_life_insurance_reminder
  // config_schema: "enabled" is always required, "timing_direction" is
  // required once enabled). This backfills the actual config to match what's
  // already on screen.
  useEffect(() => {
    if (config["enabled"] === undefined) {
      handleConfigChange("enabled", config["stage"] === "reminder_1");
    }
    if (config["timing_direction"] === undefined) {
      handleConfigChange("timing_direction", "before");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  return (
    <Stack spacing={3}>
      <Autocomplete
        options={STAGE_OPTIONS}
        value={STAGE_OPTIONS.find((o) => o.value === config["stage"]) || null}
        onChange={(e, newValue) => handleStageChange(newValue?.value)}
        getOptionLabel={(option) => option?.label || ""}
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        renderInput={(params) => (
          <TextField {...params} label="Reminder Stage" variant="outlined" required />
        )}
        noOptionsText="No stages found"
      />

      <Autocomplete
        options={YES_NO_OPTIONS}
        value={
          YES_NO_OPTIONS.find(
            (o) => o.value === (config["stage"] === "reminder_1" ? true : !!config["enabled"]),
          ) || null
        }
        disabled={config["stage"] === "reminder_1"}
        onChange={(e, newValue) => handleConfigChange("enabled", !!newValue?.value)}
        getOptionLabel={(option) => option?.label || ""}
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Enabled"
            variant="outlined"
            required
            helperText={
              config["stage"] === "reminder_1" ? "Reminder 1 is mandatory and always enabled" : ""
            }
          />
        )}
        noOptionsText="No options found"
      />

      {!isEnabled && (
        <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.grayFontColor }}>
          This reminder is disabled — no configuration is needed while it's off.
        </Typography>
      )}

      {isEnabled && (
        <>
          <Stack spacing={1.5}>
            <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
              Reminder Timing
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Send reminder"
                type="number"
                variant="outlined"
                value={config["timing_days"] ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  handleConfigChange("timing_days", raw === "" ? null : Math.max(0, Number(raw)));
                }}
                helperText="Example: 3 Days before due"
                sx={{ flex: 1 }}
              />
              <Autocomplete
                options={TIMING_DIRECTION_OPTIONS}
                value={
                  TIMING_DIRECTION_OPTIONS.find(
                    (o) => o.value === (config["timing_direction"] || "before"),
                  ) || null
                }
                onChange={(e, newValue) => handleConfigChange("timing_direction", newValue?.value)}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => <TextField {...params} variant="outlined" />}
                disableClearable
                sx={{ flex: 1 }}
              />
            </Stack>
          </Stack>

          <TextField
            label="Scheduled Send Time"
            type="time"
            variant="outlined"
            value={config["scheduled_time"] || ""}
            onChange={(e) => handleConfigChange("scheduled_time", e.target.value || "")}
            InputLabelProps={{ shrink: true }}
            helperText="Tenant-local time to actually send this reminder. Leave blank to send immediately when the cron runs."
          />

          <Autocomplete
            options={YES_NO_OPTIONS}
            value={YES_NO_OPTIONS.find((o) => o.value === !!config["capture_response"]) || null}
            onChange={(e, newValue) => handleConfigChange("capture_response", !!newValue?.value)}
            getOptionLabel={(option) => option?.label || ""}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Capture Yes/No Response"
                variant="outlined"
                helperText="If Yes, the customer is asked to confirm renewal and their reply routes back into this flow (informational only — it never changes the due date)"
              />
            )}
            noOptionsText="No options found"
          />

          <Autocomplete
            options={templates || []}
            value={selectedTemplate}
            onChange={(e, newValue) => handleTemplateChange(newValue)}
            getOptionLabel={(option) => option?.template_name || ""}
            renderOption={(props, option) => (
              <Stack
                {...props}
                key={option?.id}
                direction="column"
                gap={0.5}
                sx={{
                  alignItems: "flex-start !important",
                  textAlign: "left",
                  width: "100%",
                }}
              >
                <Typography>{option?.template_name}</Typography>
                <Typography variant="body2" color={ColorsConstants.primaryGray40}>
                  {option?.category?.[0]}
                  {option?.category?.slice(1).toLowerCase()}
                </Typography>
              </Stack>
            )}
            isOptionEqualToValue={(option, value) => option?.template_id === value?.template_id}
            renderInput={(params) => (
              <TextField {...params} label="Select Template" variant="outlined" required />
            )}
            noOptionsText="No approved templates found"
          />

          {selectedTemplate && (
            <Stack spacing={1.5}>
              <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
                Variable Mapping
              </Typography>
              {placeholderCount === 0 && (
                <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.grayFontColor }}>
                  This template has no {"{{n}}"} placeholders — nothing to map.
                </Typography>
              )}
              {Array.from({ length: placeholderCount }, (_, index) => (
                <Autocomplete
                  key={index}
                  options={LIFE_FIELD_OPTIONS}
                  value={
                    LIFE_FIELD_OPTIONS.find((o) => o.value === variableMapping[index]) || null
                  }
                  onChange={(e, newValue) => handleMappingChange(index, newValue?.value)}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) => option?.value === value?.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`{{${index + 1}}}`}
                      variant="outlined"
                      size="small"
                      required
                    />
                  )}
                  noOptionsText="No fields found"
                />
              ))}

              <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, mt: 1 }}>
                Preview
              </Typography>
              <Box
                sx={{
                  backgroundColor: "#D9FDD3",
                  borderRadius: 2,
                  p: 2,
                  maxWidth: 320,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                }}
              >
                <Typography sx={{ ...typography.bodyLarge, whiteSpace: "pre-wrap" }}>
                  {buildPreviewText(selectedTemplate, variableMapping)}
                </Typography>
              </Box>
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};

export default SendLifeInsuranceReminderPreset;
